import { Injectable } from '@angular/core';
import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { Observable, throwError, timer } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

/** Marca que este GET já teve um retry silencioso por 429 (evita loop). */
export const RATE_LIMIT_GET_RETRIED = new HttpContextToken<boolean>(() => false);

/**
 * Um retry silencioso em GET à API após 429: aguarda Retry-After (segundos) + jitter 0–5s.
 * POST/PUT/PATCH/DELETE não são reexecutados (efeitos colaterais / duplicação).
 */
@Injectable()
export class RateLimitRetryInterceptor implements HttpInterceptor {
  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: unknown) => {
        const err = error as HttpErrorResponse;
        if (request.method !== 'GET' || err?.status !== 429) {
          return throwError(() => error);
        }
        if (request.context.get(RATE_LIMIT_GET_RETRIED)) {
          return throwError(() => error);
        }
        const url = request.url;
        const isApi = url.includes(environment.apiUrl) || url.startsWith('/api/');
        if (!isApi) {
          return throwError(() => error);
        }
        const raw = err.headers?.get('retry-after');
        const parsed = raw != null && raw !== '' ? Number.parseInt(raw, 10) : Number.NaN;
        const retryAfterSec = Number.isFinite(parsed) ? Math.min(Math.max(parsed, 0), 120) : 1;
        const jitterMs = Math.floor(Math.random() * 5001);
        const delayMs = retryAfterSec * 1000 + jitterMs;
        const retried = request.clone({
          context: request.context.set(RATE_LIMIT_GET_RETRIED, true)
        });
        return timer(delayMs).pipe(switchMap(() => next.handle(retried)));
      })
    );
  }
}
